import { useState, useRef, useMemo } from 'react';
import { useSelector } from '@legendapp/state/react';
import { useTranslation } from 'react-i18next';
import { X, Plus } from 'lucide-react';
import {
  ui$,
  closeModal,
  articles$,
  updateArticleTags,
  getRecentTags
} from '../store';
import { Badge, BadgeButton, Button, Dialog, Field, IconButton, Input, ICON_SIZE } from '../design-system';
import { normalizeTag } from '../utils/normalizeTag';
import './TagEditModal.css';

const TagEditModal = () => {
  const { t } = useTranslation('articles');
  const modalData = useSelector(() => ui$.modalData.get());
  const articleId = modalData?.articleId;

  // Get article's current tags
  const article = useSelector(() => articles$[articleId]?.get());
  const currentTags = article?.tags || [];

  // Local state for editing — 弹窗按次挂载，以文章当前标签初始化即可。
  const [tags, setTags] = useState(currentTags);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  // Get suggestions (frequent tags not already selected)
  const suggestions = useMemo(() => {
    const recentTags = getRecentTags(15);
    return recentTags.filter(tag => !tags.includes(tag));
  }, [tags]);

  // 焦点由 Dialog 的 initialFocusRef 在打开时给（替代 setTimeout hack）。

  const handleAddTag = () => {
    const normalized = normalizeTag(inputValue);
    if (normalized && !tags.includes(normalized)) {
      setTags([...tags, normalized]);
      setInputValue('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleSuggestionClick = (tag) => {
    if (!tags.includes(tag)) {
      setTags([...tags, tag]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSave = () => {
    updateArticleTags(articleId, tags);
    closeModal();
  };

  return (
    <Dialog
      isOpen={true}
      onClose={closeModal}
      title={t('tags.editTitle', 'Edit Tags')}
      size="sm"
      initialFocusRef={inputRef}
      footer={
        <>
          <Button variant="neutral" onClick={closeModal}>
            {t('common:cancel', 'Cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {t('common:done', 'Done')}
          </Button>
        </>
      }
    >
      <div className="tag-edit-content">
        {/* Current Tags */}
        <div className="tag-edit-section">
          <div className="tag-edit-label">{t('tags.current', 'Current Tags')}</div>
          <div className="tag-edit-current">
            {tags.length === 0 ? (
              <span className="tag-edit-empty">{t('tags.noTags', 'No tags yet')}</span>
            ) : (
              tags.map(tag => (
                <Badge key={tag} variant="solid-black">
                  {tag}
                  <BadgeButton
                    onClick={() => handleRemoveTag(tag)}
                    aria-label={`${t('common:button.remove')} ${tag}`}
                  >
                    <X size={ICON_SIZE.xs} aria-hidden="true" />
                  </BadgeButton>
                </Badge>
              ))
            )}
          </div>
        </div>

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div className="tag-edit-section">
            <div className="tag-edit-label">{t('tags.suggestions', 'Suggestions')}</div>
            <div className="tag-edit-suggestions">
              {suggestions.map(tag => (
                <Badge
                  key={tag}
                  as="button"
                  variant="solid-grey"
                  onClick={() => handleSuggestionClick(tag)}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Add New Tag Input */}
        <Field label={t('tags.addNew', 'Add New Tag')}>
          {(inputProps) => (
            <div className="tag-edit-input-row">
              <Input
                {...inputProps}
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('tags.placeholder', 'Type tag name...')}
              />
              <IconButton
                variant="neutral"
                label={t('tags.addNew', 'Add New Tag')}
                onClick={handleAddTag}
                disabled={!inputValue.trim()}
              >
                <Plus size={ICON_SIZE.sm} />
              </IconButton>
            </div>
          )}
        </Field>
      </div>
    </Dialog>
  );
};

export default TagEditModal;
